import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from 'react-toastify';

/** Expected cancellations occur when a query loses its observer or its key changes. */
export const isRequestCancellation = (error) => (
  error?.code === 'ERR_CANCELED' ||
  error?.code === 'REQUEST_ABORTED' ||
  error?.name === 'CanceledError' ||
  error?.name === 'CancelledError'
);

/**
 * Determine whether a failed query should be retried based on error status
 * Avoid retrying client/auth errors (401, 403, 404), retry transient network or server errors.
 */
export const shouldRetry = (failureCount, error) => {
  if (isRequestCancellation(error)) return false;
  if (failureCount >= 2) return false;

  const status = error?.status ?? error?.statusCode ?? error?.response?.status;
  if (status === 401 || status === 403 || status === 404 || status === 422) {
    return false;
  }

  // If explicit isAuthError or validation error, don't retry
  if (error?.isAuthError) return false;

  return true;
};

/**
 * Global Query Cache error handler
 */
const handleQueryError = (error, query) => {
  // Query cancellation is lifecycle control, not an application failure.
  if (isRequestCancellation(error)) return;
  // Only trigger global toasts if query doesn't handle errors locally via meta
  if (query?.meta?.suppressGlobalToast) return;

  const status = error?.status ?? error?.response?.status;
  // 401/403 are handled by auth interceptors/redirects
  if (status === 401 || status === 403) return;

  const message = error?.message || error?.response?.data?.message || 'Failed to fetch data';
  console.error(`[Query Error] Key: ${JSON.stringify(query.queryKey)}`, error);
  
  if (query?.meta?.showToastOnError) {
    toast.error(message);
  }
};

/**
 * Global Mutation Cache error handler
 */
const handleMutationError = (error, _variables, _context, mutation) => {
  if (isRequestCancellation(error)) return;
  if (mutation?.meta?.suppressGlobalToast) return;

  const message = error?.message || error?.response?.data?.message || 'Operation failed';
  console.error('[Mutation Error]', error);

  if (mutation?.meta?.showToastOnError !== false) {
    toast.error(message);
  }
};

/**
 * Factory function to create a new configured QueryClient.
 * Useful for tests and SSR/isolation.
 */
export function createQueryClient(options = {}) {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: handleQueryError,
    }),
    mutationCache: new MutationCache({
      onError: handleMutationError,
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 30 * 60 * 1000,    // 30 minutes
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: shouldRetry,
        ...options.queries,
      },
      mutations: {
        retry: 0,
        ...options.mutations,
      },
    },
  });
}

/** Default application singleton QueryClient */
export const queryClient = createQueryClient();

export default queryClient;
